import {
  Box,
  EmptyState,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@salestrak/ui";
import { formatDateShort } from "#app/lib/utils/format-date";
import {
  InitialsAvatar,
  PanelCard,
  ScreenHeader,
  TableRowsSkeleton,
} from "./admin-ui";
import type { AdminWorkspace } from "./use-admin-data";

export function WorkspacesScreen({
  workspaces,
  loading,
}: {
  workspaces: AdminWorkspace[];
  loading: boolean;
}) {
  const totalMembers = workspaces.reduce((sum, w) => sum + w.memberCount, 0);

  return (
    <Box fill="flex" overflow="auto">
      <ScreenHeader
        title="Workspaces"
        meta={`${workspaces.length} workspaces · ${totalMembers.toLocaleString()} members`}
      />
      <Box paddingX="lg" paddingBottom="xl" paddingTop="sm">
        <PanelCard>
          {/* `table-fixed`: the px columns keep their width and the unsized
              ones share what is left, and a bounded cell is what lets a long
              name ellipsize instead of widening the table. */}
          <Table layout="fixed">
            <TableHeader>
              <TableRow>
                <TableHead>Workspace</TableHead>
                <TableHead>URL</TableHead>
                <TableHead width={90} align="end">Members</TableHead>
                <TableHead width={110}>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && workspaces.length === 0 ? (
                <TableRowsSkeleton
                  rows={4}
                  columns={[{ fill: true }, { fill: true }, { width: 90, align: "end" }, { width: 110 }]}
                />
              ) : null}

              {workspaces.map((w, index) => (
                <TableRow key={`${w.id}-${index}`}>
                  <TableCell>
                    <Box direction="row" align="center" gap="sm">
                      <InitialsAvatar name={w.name} shape="square" />
                      <Box direction="column">
                        <Text size="sm" weight="medium" truncate={1}>
                          {w.name}
                        </Text>
                        <Text size="xs" variant="muted" mono truncate={1}>
                          /{w.urlPath}
                        </Text>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box direction="row" align="center" gap="xs">
                      <Icon name="link" size="xs" color="muted" />
                      <Text size="sm" mono variant="info" truncate={1}>
                        {w.urlPath}
                      </Text>
                    </Box>
                  </TableCell>
                  <TableCell align="end">
                    <Text size="sm" weight="medium" mono>
                      {w.memberCount}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text size="sm" variant="muted" mono>
                      {formatDateShort(w.createdAt)}
                    </Text>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!loading && workspaces.length === 0 ? (
            <EmptyState size="xs" title="No workspaces found" />
          ) : null}
        </PanelCard>
      </Box>
    </Box>
  );
}
